import { faMegaphone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import useFeedback from 'components/Feedback/useFeedback';
import { GetAllWaitingLineTicketsItem } from 'lib/graphql/entities/tickets/types';
import { QueueType, TicketStatus, useCallTicketMutation } from 'lib/graphql/graphql';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

function boldText(text: React.ReactNode) {
  return <b>{text}</b>;
}

interface CallTicketItemProps {
  onCall: () => void
  ticket: GetAllWaitingLineTicketsItem
}

function CallTicketItem(props: CallTicketItemProps) {
  const { onCall, ticket } = props;

  const feedback = useFeedback();

  const [callTicket] = useCallTicketMutation();

  const [modalOpen, setModalOpen] = useState(false);

  const handleCallTicket = useCallback(async () => {
    try {
      await callTicket({
        variables: {
          ticketId: ticket.id,
          waitingLineId: ticket.waitingLineId,
        },
      });
      onCall();
      setModalOpen(false);
      feedback({
        anchor: { horizontal: 'right', vertical: 'bottom' },
        content: (
          <FormattedMessage
            id="ticket_called"
            values={{ displayId: ticket.displayId, b: boldText }}
          />
        ),
        severity: 'success',
      });
    } catch (err) {
      const error = err as Error;
      let errorId = '';
      switch (error.message) {
        case 'Ticket is already called':
          errorId = 'operating_ticket_already_called';
          break;
        case 'Invalid ticket':
          errorId = 'operating_unknown_error';
          break;
        default:
          errorId = 'operating_ticket_call_generic_error';
          break;
      }

      feedback({
        anchor: { horizontal: 'right', vertical: 'bottom' },
        content: (<FormattedMessage id={errorId} />),
        severity: 'error',
      });
    }
  }, [callTicket, feedback, onCall, ticket]);

  const disabled = ticket.status === TicketStatus.Validated
    || ticket.queueType === QueueType.Called
    || ticket.queueType === QueueType.NoShow;

  return (
    <>
      <MenuItem
        disabled={disabled}
        onClick={() => setModalOpen(true)}
      >
        <ListItemIcon>
          <FontAwesomeIcon icon={faMegaphone} size="sm" />
        </ListItemIcon>
        <ListItemText>
          <FormattedMessage id="ticket_call" />
        </ListItemText>
      </MenuItem>
      <ConfirmModal
        title="ticket_call"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleCallTicket}
        closeText="general_no"
        confirmText="general_yes"
      >
        <FormattedMessage id="ticket_call_body" values={{ displayId: ticket.displayId, b: boldText }} />
      </ConfirmModal>
    </>
  );
}

export default CallTicketItem;
